import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import Sortable from 'sortablejs';
import { Modal } from 'bootstrap';
import { Toast } from 'rad_common/toast';

export default class extends Controller {
  static targets = [
    'lane',
    'sidebar',
    'backlogList',
    'searchList',
    'sidebarSearch',
    'sidebarStatus',
    'sidebarPanel',
    'nav',
    'sectionTab',
    'section',
  ];
  static values = { url: String, backlogUrl: String, removeUrl: String, storageKey: String };

  connect() {
    this.sortables = this.laneTargets.map((lane) =>
      Sortable.create(lane, {
        group: 'board',
        animation: 150,
        draggable: '.board-card',
        ghostClass: 'sortable-ghost',
        filter: '.btn-group, .board-timer',
        preventOnFilter: false,
        onEnd: this.onEnd.bind(this),
      })
    );

    if (this.hasSidebarTarget) {
      this.sortables.push(this.pullSortable(this.sidebarTarget));

      this.restoreSidebarState();
      this.filterSidebar();
    }
  }

  disconnect() {
    (this.sortables || []).forEach((sortable) => sortable.destroy());
  }

  // sidebar lists feed cards into the lanes and never reorder themselves; only the backlog also
  // accepts drops from the lanes (put), since it acts like an off-board lane of its own
  pullSortable(element, put = false) {
    return Sortable.create(element, {
      group: { name: 'board', pull: true, put },
      sort: false,
      animation: 150,
      draggable: '.board-card',
      ghostClass: 'sortable-ghost',
      filter: '.btn-group, .board-timer',
      preventOnFilter: false,
      onEnd: this.onEnd.bind(this),
    });
  }

  // the search results arrive in a lazy turbo frame, so the list is only draggable once it lands
  searchListTargetConnected(element) {
    this.searchSortable = this.pullSortable(element);
  }

  searchListTargetDisconnected() {
    if (this.searchSortable) {
      this.searchSortable.destroy();
      this.searchSortable = null;
    }
  }

  backlogListTargetConnected(element) {
    this.backlogSortable = this.pullSortable(element, true);
  }

  backlogListTargetDisconnected() {
    if (this.backlogSortable) {
      this.backlogSortable.destroy();
      this.backlogSortable = null;
    }
  }

  closeSearchFilters() {
    const modal = Modal.getInstance(document.getElementById('board-task-search-modal'));

    if (modal) {
      modal.hide();
    }
  }

  restoreSidebarState() {
    const status = this.readState('status');

    if (status) {
      this.sidebarStatusTarget.value = status;
    }

    if (this.readState('nav_expanded') === 'true') {
      this.expandNav(this.readState('section') || 'recent');
    }
  }

  // the hamburger expands the sidebar to reveal the section tabs (and opens the last section),
  // or collapses everything back down to just the hamburger
  toggleNav() {
    if (this.navTarget.classList.contains('board-nav-expanded')) {
      this.collapseNav();
    } else {
      this.expandNav(this.readState('section') || 'recent');
    }
  }

  expandNav(section) {
    this.navTarget.classList.add('board-nav-expanded');
    this.writeState('nav_expanded', 'true');
    this.activateSection(section);
  }

  collapseNav() {
    this.navTarget.classList.remove('board-nav-expanded');

    if (this.hasSidebarPanelTarget) {
      this.sidebarPanelTarget.classList.remove('open');
    }

    this.writeState('nav_expanded', 'false');
  }

  // clicking a tab expands the sidebar (if needed) and opens that section
  showSection(event) {
    this.navTarget.classList.add('board-nav-expanded');
    this.writeState('nav_expanded', 'true');
    this.activateSection(event.currentTarget.dataset.section);
  }

  activateSection(section) {
    this.sectionTabTargets.forEach((tab) => tab.classList.toggle('active', tab.dataset.section === section));
    this.sectionTargets.forEach((pane) => pane.classList.toggle('d-none', pane.dataset.section !== section));

    if (this.hasSidebarPanelTarget) {
      this.sidebarPanelTarget.classList.add('open');
    }

    this.writeState('section', section);
  }

  readState(name) {
    try {
      return window.localStorage.getItem(`${this.storageKeyValue}.${name}`);
    } catch {
      return null;
    }
  }

  writeState(name, value) {
    try {
      window.localStorage.setItem(`${this.storageKeyValue}.${name}`, value);
    } catch {
      // storage unavailable (private mode, quota) — the board still works, it just won't remember
    }
  }

  async onEnd(event) {
    if (event.from === event.to) {
      return;
    }

    const card = event.item;
    const taskId = card.dataset.taskId;
    const toBacklog = this.hasBacklogListTarget && event.to === this.backlogListTarget;
    const lane = event.to.dataset.lane;
    const url = (toBacklog ? this.backlogUrlValue : this.urlValue).replace('TASK_ID', taskId);
    const destination = toBacklog ? 'the backlog' : lane;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lane, card_id: card.id }),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      Turbo.renderStreamMessage(await response.text());
      this.updateCount(event.from);
      this.updateCount(event.to);
      Toast.success('Moved', `The task was moved to ${destination}.`);
    } catch {
      const reference = event.from.children[event.oldIndex] || null;
      event.from.insertBefore(card, reference);
      Toast.error('Error', 'The task could not be moved.');
    }
  }

  async remove(event) {
    const card = event.currentTarget.closest('.board-card');
    const lane = card.closest('.board-lane');
    const url = this.removeUrlValue.replace('TASK_ID', card.dataset.taskId);

    if (!window.confirm('Remove this task from the board?')) {
      return;
    }

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      Turbo.renderStreamMessage(await response.text());
      this.updateCount(lane);
      Toast.success('Removed', 'The task was removed from the board.');
    } catch {
      Toast.error('Error', 'The task could not be removed from the board.');
    }
  }

  filterSidebar() {
    const search = this.sidebarSearchTarget.value.trim().toLowerCase();
    const status = this.sidebarStatusTarget.value;

    this.writeState('status', status);

    this.sidebarTarget.querySelectorAll('.board-card').forEach((card) => {
      const active = card.dataset.active === 'true';
      const nameMismatch = search.length > 1 && !card.dataset.taskName.toLowerCase().includes(search);
      const statusMismatch = (status === 'active' && !active) || (status === 'inactive' && active);

      card.classList.toggle('d-none', nameMismatch || statusMismatch);
    });
  }

  updateCount(lane) {
    if (!lane.dataset.lane) {
      return;
    }

    const badge = document.getElementById(`lane_count_${lane.dataset.lane.replace(/\W+/g, '_').toLowerCase()}`);

    if (badge) {
      badge.textContent = lane.querySelectorAll('.board-card').length;
    }
  }
}
